import { useState, useCallback, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../adapters/powersync';
import { forceResync } from '../sync/syncActions';
import { useUploadQueueStats } from '../sync/useUploadQueueStats';
import { usePatientTabs } from './PatientTabsContext';
import { ManagePatientTabsDrawer } from './ManagePatientTabsDrawer';
import { OpenPatientDrawer } from './OpenPatientDrawer';
import { usePinnedRoutes } from './usePinnedRoutes';

// ─── Color utilities — derive theme tokens from env vars ───

export function hexToRgb(hex: string): [number, number, number] | null {
  const m = hex.replace('#', '').match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return null;
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

export function rgba(rgb: [number, number, number], alpha: number) {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

export function darken(rgb: [number, number, number], amount: number): [number, number, number] {
  return [
    Math.max(0, Math.round(rgb[0] * (1 - amount))),
    Math.max(0, Math.round(rgb[1] * (1 - amount))),
    Math.max(0, Math.round(rgb[2] * (1 - amount))),
  ];
}

// ─── Env-derived constants ───

export const primaryHex = import.meta.env.VITE_PRIMARY_COLOR || '#2563eb';
export const secondaryHex = import.meta.env.VITE_SECONDARY_COLOR || '';
export const themeVariant = (import.meta.env.VITE_THEME_VARIANT || 'clinical') as
  | 'clinical'
  | 'premium'
  | 'spa';
export const primaryRgb = hexToRgb(primaryHex) ?? [37, 99, 235];
export const secondaryRgb = secondaryHex ? hexToRgb(secondaryHex) : null;

export const orgName = import.meta.env.VITE_ORG_NAME || 'Clinic';
export const orgLogoUrl = import.meta.env.VITE_ORG_LOGO_URL || '';
export const orgLogoHorizontalUrl = import.meta.env.VITE_ORG_LOGO_HORIZONTAL_URL || '';
export const orgPhone = import.meta.env.VITE_SUPPORT_PHONE || '';
export const orgEmail = import.meta.env.VITE_SUPPORT_EMAIL || '';

// ─── App background gradient ───

/**
 * Compute a variant-aware app background gradient.
 *
 * - `spa`      — Warm cream base with two soft radial blobs (primary + secondary tints).
 * - `premium`  — Clean white base with a subtle corner tint for depth.
 * - `clinical` — Near-flat cool-neutral wash (minimal distraction).
 */
export function buildAppGradient(
  p: [number, number, number],
  s: [number, number, number] | null,
  variant: 'clinical' | 'premium' | 'spa',
): string {
  const sec = s ?? p;

  switch (variant) {
    case 'spa':
      return [
        'linear-gradient(160deg, rgba(255,248,240,0.965) 0%, rgba(255,255,255,0.92) 40%, rgba(248,250,252,0.965) 100%)',
        `radial-gradient(980px 560px at 16% 10%, ${rgba(p, 0.14)} 0%, ${rgba(p, 0.0)} 72%)`,
        `radial-gradient(860px 560px at 88% 18%, ${rgba(sec, 0.11)} 0%, ${rgba(sec, 0.0)} 70%)`,
        `radial-gradient(920px 620px at 22% 78%, ${rgba(p, 0.06)} 0%, ${rgba(p, 0.0)} 68%)`,
      ].join(', ');

    case 'premium':
      return [
        'linear-gradient(170deg, rgba(255,252,246,0.985) 0%, rgba(248,250,252,0.965) 100%)',
        `radial-gradient(900px 560px at 12% 6%, ${rgba(p, 0.08)} 0%, ${rgba(p, 0.0)} 62%)`,
        `radial-gradient(720px 480px at 90% 12%, ${rgba(sec, 0.06)} 0%, ${rgba(sec, 0.0)} 58%)`,
        `radial-gradient(980px 640px at 18% 78%, ${rgba(p, 0.04)} 0%, ${rgba(p, 0.0)} 70%)`,
      ].join(', ');

    case 'clinical':
    default:
      return [
        'linear-gradient(175deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.96) 100%)',
        `radial-gradient(700px 400px at 15% 10%, ${rgba(p, 0.03)} 0%, ${rgba(p, 0.0)} 50%)`,
      ].join(', ');
  }
}

// ─── Avatar gradient from a hex color ───

export function avatarGradient(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const light: [number, number, number] = [
    Math.min(255, Math.round(rgb[0] + (255 - rgb[0]) * 0.3)),
    Math.min(255, Math.round(rgb[1] + (255 - rgb[1]) * 0.3)),
    Math.min(255, Math.round(rgb[2] + (255 - rgb[2]) * 0.3)),
  ];
  const dark = darken(rgb, 0.3);
  return `linear-gradient(135deg, rgb(${light[0]}, ${light[1]}, ${light[2]}), rgb(${dark[0]}, ${dark[1]}, ${dark[2]}))`;
}

// ─── Brand tokens hook ───

/** CSS custom property overrides derived from brand env vars */
export function useBrandTokens(): Record<string, string> {
  return useMemo(() => {
    const p = primaryRgb;
    const pDark = darken(p, 0.15);
    const gradEnd = secondaryRgb ?? p;

    const navActiveAlphaStart = themeVariant === 'spa' ? 0.16 : 0.1;
    const navActiveAlphaEnd = themeVariant === 'spa' ? 0.22 : 0.15;

    const ink = [15, 23, 42] as const;
    const borderAlpha = themeVariant === 'premium' ? 0.11 : themeVariant === 'spa' ? 0.1 : 0.12;
    const borderStrongAlpha =
      themeVariant === 'premium' ? 0.16 : themeVariant === 'spa' ? 0.14 : 0.18;

    return {
      '--color-primary': primaryHex,
      '--color-primary-hover': `rgb(${pDark[0]}, ${pDark[1]}, ${pDark[2]})`,
      '--color-primary-soft': rgba(p, 0.12),
      '--color-ring': rgba(p, 0.35),
      '--border': rgba(ink as unknown as [number, number, number], borderAlpha),
      '--border-strong': rgba(ink as unknown as [number, number, number], borderStrongAlpha),
      '--bg-primary-subtle': rgba(p, 0.08),
      '--gradient-primary': `linear-gradient(135deg, ${primaryHex}, rgb(${pDark[0]}, ${pDark[1]}, ${pDark[2]}))`,
      '--gradient-cta': `linear-gradient(135deg, ${primaryHex}, rgb(${pDark[0]}, ${pDark[1]}, ${pDark[2]}))`,
      '--gradient-nav-active': `linear-gradient(135deg, ${rgba(p, navActiveAlphaStart)}, ${rgba(gradEnd, navActiveAlphaEnd)})`,
      '--gradient-patient-pill': `linear-gradient(135deg, ${rgba(p, 0.08)}, ${rgba(p, 0.15)})`,
      '--bg-app-gradient': buildAppGradient(p, secondaryRgb, themeVariant),
      '--bg-chrome-gradient': '#dfe3ea',
      '--sidebar-edge-color': rgba(p, 0.4),
      '--sidebar-edge-gradient-from': rgba(p, 0.12),
      '--sidebar-edge-shadow': `0 0 4px ${rgba(p, 0.3)}`,
    };
  }, []);
}

// ─── Sidebar state hook with localStorage persistence ───

const STORAGE_KEY = 'powercareai:sidebar-collapsed';

export function useSidebarState(storageKey = STORAGE_KEY) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, String(next));
      } catch {
        /* noop */
      }
      return next;
    });
  }, [storageKey]);

  return { collapsed, toggle };
}

// ─── Resizable sidebar hook ───

export const SIDEBAR_COLLAPSED_WIDTH = 64;

export function useResizableSidebar({
  storageKey,
  min = 280,
  max = 520,
  defaultWidth = 360,
  collapsed = false,
  onExpand,
}: {
  storageKey: string;
  min?: number;
  max?: number;
  defaultWidth?: number;
  collapsed?: boolean;
  onExpand?: () => void;
}) {
  const [width, setWidth] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v ? Math.max(min, Math.min(max, Number(v))) : defaultWidth;
    } catch {
      return defaultWidth;
    }
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{
    x0: number;
    w0: number;
    moved: boolean;
    wasCollapsed: boolean;
    pointerId: number;
    el: HTMLElement;
  } | null>(null);

  // Use refs for callbacks so the element listeners always see latest state
  const widthRef = useRef(width);
  widthRef.current = width;
  const onExpandRef = useRef(onExpand);
  onExpandRef.current = onExpand;

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.x0;
      if (Math.abs(dx) > 4) dragRef.current.moved = true;
      if (dragRef.current.moved) {
        const newW = Math.max(min, Math.min(max, dragRef.current.w0 + dx));
        if (dragRef.current.wasCollapsed && newW > SIDEBAR_COLLAPSED_WIDTH + 40) {
          dragRef.current.wasCollapsed = false;
          onExpandRef.current?.();
        }
        setWidth(newW);
      }
    },
    [min, max],
  );

  const handlePointerUp = useCallback(() => {
    if (!dragRef.current) return;
    const { el, pointerId, moved } = dragRef.current;
    el.releasePointerCapture(pointerId);
    el.removeEventListener('pointermove', handlePointerMove);
    el.removeEventListener('pointerup', handlePointerUp);
    setDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    if (moved) {
      try {
        localStorage.setItem(storageKey, String(widthRef.current));
      } catch {
        /* noop */
      }
    }
    dragRef.current = null;
  }, [handlePointerMove, storageKey]);

  const startDrag = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const el = e.currentTarget as HTMLElement;
      el.setPointerCapture(e.pointerId);
      el.addEventListener('pointermove', handlePointerMove);
      el.addEventListener('pointerup', handlePointerUp);
      dragRef.current = {
        x0: e.clientX,
        w0: collapsed ? SIDEBAR_COLLAPSED_WIDTH : width,
        moved: false,
        wasCollapsed: collapsed,
        pointerId: e.pointerId,
        el,
      };
      setDragging(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width, collapsed, handlePointerMove, handlePointerUp],
  );

  const resetWidth = useCallback(() => {
    setWidth(defaultWidth);
    try {
      localStorage.setItem(storageKey, String(defaultWidth));
    } catch {
      /* noop */
    }
  }, [defaultWidth, storageKey]);

  return { width, dragging, startDrag, resetWidth, setWidth };
}

// ─── Sidebar Toggle Button (panel-left icon) ───

export function SidebarToggleButton({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
      aria-label={collapsed ? 'Show sidebar' : 'Hide sidebar'}
      style={{
        width: 32,
        height: 32,
        borderRadius: 'var(--radius-md)',
        border: '1px solid transparent',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        color: 'var(--text-muted)',
        flexShrink: 0,
        transition: 'background 150ms, border-color 150ms, color 150ms',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--bg-surface-2)';
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.color = 'var(--text)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.borderColor = 'transparent';
        e.currentTarget.style.color = 'var(--text-muted)';
      }}
    >
      <NavIcon name={collapsed ? 'panel-left-open' : 'panel-left-close'} />
    </button>
  );
}

// ─── Resize Gutter ───

export function ResizeGutter({
  onDragStart,
  onDoubleClick,
}: {
  onDragStart?: (e: React.PointerEvent) => void;
  onDoubleClick?: () => void;
}) {
  return (
    <div
      role="separator"
      aria-label="Resize sidebar"
      onPointerDown={onDragStart}
      onDoubleClick={onDoubleClick}
      style={{
        // Visual footprint stays narrow (line + grip are centered), but the
        // hit area is wider so it's comfortable on touch devices where a
        // 12px target is below the ~44px recommended minimum.
        width: 24,
        marginLeft: -6,
        marginRight: -6,
        flexShrink: 0,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'col-resize',
        touchAction: 'none',
      }}
    >
      {/* Vertical line */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          width: 1,
          background: 'var(--border)',
          transform: 'translateX(-50%)',
        }}
      />

      {/* Grip handle — always visible */}
      <div
        title="Adjust Sidebar Width"
        style={{
          position: 'relative',
          zIndex: 10,
          width: 16,
          height: 28,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <NavIcon name="grip-vertical" />
      </div>
    </div>
  );
}

// ─── Right Drawer ───

export function RightDrawer({
  open,
  onClose,
  title,
  icon,
  children,
  expandable = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  icon: string;
  children: React.ReactNode;
  /** When true, show an expand/collapse button that toggles between default and half-page width */
  expandable?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const width = expanded ? '50%' : 420;

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset to collapsed when drawer closes
  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: open ? width : 0,
        maxWidth: '100%',
        zIndex: 40,
        overflow: 'hidden',
        transition: 'width 200ms ease-out',
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '100vw',
          height: '100%',
          background: '#ffffff',
          borderLeft: '1px solid var(--border)',
          boxShadow: open ? '-4px 0 16px rgba(0,0,0,0.08)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Sticky header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            padding: 'var(--sp-4) var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <div style={{ color: 'var(--color-primary)' }}>
            <NavIcon name={icon} />
          </div>
          <span
            style={{
              flex: 1,
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-base)',
              color: 'var(--text)',
            }}
          >
            {title}
          </span>
          {expandable && (
            <button
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? 'Collapse drawer' : 'Expand drawer'}
              title={expanded ? 'Collapse' : 'Expand'}
              style={{
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {expanded ? (
                  <>
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="14" y1="10" x2="21" y2="3" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </>
                ) : (
                  <>
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </>
                )}
              </svg>
            </button>
          )}
          <button
            onClick={onClose}
            aria-label="Close drawer"
            style={{
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 'var(--sp-4) var(--sp-5)',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Page Header with Sidebar Toggle ───

/**
 * Reusable page header that includes the sidebar toggle button.
 * Use in any page rendered inside a shell layout (Clinic/Patient mode).
 *
 * Usage:
 *   const { onToggleSidebar, collapsed } = useOutletContext<ShellOutletContext>();
 *   <PageHeader title="Forms" subtitle="Manage form templates" collapsed={collapsed} onToggle={onToggleSidebar}>
 *     <button>+ New Form</button>
 *   </PageHeader>
 */
export type ShellOutletContext = {
  collapsed: boolean;
  onToggleSidebar?: () => void;
};

export function PageHeader({
  title,
  subtitle,
  collapsed,
  onToggle,
  children,
}: {
  title: string;
  subtitle?: string;
  collapsed: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        {onToggle && <SidebarToggleButton collapsed={collapsed} onToggle={onToggle} />}
        <div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              margin: 0,
              lineHeight: 'var(--line-height-xl)',
            }}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                margin: '4px 0 0',
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {children && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexShrink: 0 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Inline SVG Icons ───

export function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: 'block' }}>
      <path
        d={direction === 'left' ? 'M10 12L6 8L10 4' : 'M6 4L10 8L6 12'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function GearIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function NavIcon({ name }: { name: string }) {
  const props = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    style: { display: 'block', flexShrink: 0 } as CSSProperties,
  };

  switch (name) {
    case 'user':
      return (
        <svg {...props}>
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case 'users':
      return (
        <svg {...props}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'user-plus':
      return (
        <svg {...props}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <line x1="19" y1="8" x2="19" y2="14" />
          <line x1="22" y1="11" x2="16" y2="11" />
        </svg>
      );
    case 'building':
      return (
        <svg {...props}>
          <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
          <path d="M9 22v-4h6v4M9 2v4M15 2v4M9 10h6M9 14h6" />
        </svg>
      );
    case 'plug':
      return (
        <svg {...props}>
          <path d="M12 22v-5M9 8V2M15 8V2M7 8h10v4a5 5 0 0 1-10 0V8z" />
        </svg>
      );
    case 'refresh':
      return (
        <svg {...props}>
          <path d="M21 2v6h-6" />
          <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
          <path d="M3 22v-6h6" />
          <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
        </svg>
      );
    case 'info':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
      );
    case 'home':
      return (
        <svg {...props}>
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9,22 9,12 15,12 15,22" />
        </svg>
      );
    case 'map-pin':
      return (
        <svg {...props}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      );
    case 'phone':
      return (
        <svg {...props}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      );
    case 'mail':
      return (
        <svg {...props}>
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="M22 7l-10 7L2 7" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      );
    case 'list':
      return (
        <svg {...props}>
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      );
    case 'list-ordered':
      return (
        <svg {...props}>
          <line x1="10" y1="6" x2="21" y2="6" />
          <line x1="10" y1="12" x2="21" y2="12" />
          <line x1="10" y1="18" x2="21" y2="18" />
          <path d="M4 6h1v4" />
          <path d="M4 10h2" />
          <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
        </svg>
      );
    case 'star':
      return (
        <svg {...props}>
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      );
    case 'clipboard':
      return (
        <svg {...props}>
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
        </svg>
      );
    case 'activity':
      return (
        <svg {...props}>
          <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
        </svg>
      );
    case 'bell':
      return (
        <svg {...props}>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
    case 'sparkles':
      return (
        <svg {...props}>
          <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
          <path d="M18 14l.75 2.25L21 17l-2.25.75L18 20l-.75-2.25L15 17l2.25-.75L18 14z" />
        </svg>
      );
    case 'chevron-down':
      return (
        <svg {...props}>
          <polyline points="6,9 12,15 18,9" />
        </svg>
      );
    case 'plus':
      return (
        <svg {...props}>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      );
    case 'layout-grid':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      );
    case 'bar-chart':
      return (
        <svg {...props}>
          <line x1="12" y1="20" x2="12" y2="10" />
          <line x1="18" y1="20" x2="18" y2="4" />
          <line x1="6" y1="20" x2="6" y2="16" />
        </svg>
      );
    case 'credit-card':
      return (
        <svg {...props}>
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </svg>
      );
    case 'peso':
      return (
        <svg {...props}>
          {/* ₱ — Philippine peso sign: vertical stem + P bowl + two horizontal bars */}
          <path d="M7 4v16" />
          <path d="M7 4h6.5a4.5 4.5 0 0 1 0 9H7" />
          <path d="M4 9h12" />
          <path d="M4 13h12" />
        </svg>
      );
    case 'message-square':
      return (
        <svg {...props}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'file-text':
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14,2 14,8 20,8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10,9 9,9 8,9" />
        </svg>
      );
    case 'heart':
      return (
        <svg {...props}>
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      );
    case 'alert-triangle':
      return (
        <svg {...props}>
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'edit':
      return (
        <svg {...props}>
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      );
    case 'check-circle':
      return (
        <svg {...props}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22,4 12,14.01 9,11.01" />
        </svg>
      );
    case 'dots-vertical':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="1" />
          <circle cx="12" cy="5" r="1" />
          <circle cx="12" cy="19" r="1" />
        </svg>
      );
    case 'save':
      return (
        <svg {...props}>
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
          <polyline points="17,21 17,13 7,13 7,21" />
          <polyline points="7,3 7,8 15,8" />
        </svg>
      );
    case 'gear':
      return (
        <svg {...props}>
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'search':
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      );
    case 'x':
      return (
        <svg {...props}>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    case 'shield-alert':
      return (
        <svg {...props}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
    case 'clock':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );
    case 'trash':
      return (
        <svg {...props}>
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      );
    case 'stethoscope':
      return (
        <svg {...props}>
          <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
          <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
          <circle cx="20" cy="10" r="2" />
        </svg>
      );
    case 'more-vertical':
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="1" fill="currentColor" />
          <circle cx="12" cy="5" r="1" fill="currentColor" />
          <circle cx="12" cy="19" r="1" fill="currentColor" />
        </svg>
      );
    case 'folder-open':
      return (
        <svg {...props}>
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'upload':
      return (
        <svg {...props}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17,8 12,3 7,8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      );
    case 'camera':
      return (
        <svg {...props}>
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
      );
    case 'chevron-left':
      return (
        <svg {...props}>
          <polyline points="15,18 9,12 15,6" />
        </svg>
      );
    case 'chevron-right':
      return (
        <svg {...props}>
          <polyline points="9,6 15,12 9,18" />
        </svg>
      );
    case 'sliders':
      return (
        <svg {...props}>
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>
      );
    case 'refresh-cw':
      return (
        <svg {...props}>
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      );
    case 'log-out':
      return (
        <svg {...props}>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      );
    case 'copy':
      return (
        <svg {...props}>
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      );
    case 'check':
      return (
        <svg {...props}>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'panel-left-close':
      return (
        <svg {...props}>
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M9 3v18" />
          <path d="m16 15-3-3 3-3" />
        </svg>
      );
    case 'panel-left-open':
      return (
        <svg {...props}>
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M9 3v18" />
          <path d="m14 9 3 3-3 3" />
        </svg>
      );
    case 'grip-vertical':
      return (
        <svg {...props}>
          <circle cx="9" cy="12" r="1" fill="currentColor" stroke="none" />
          <circle cx="9" cy="5" r="1" fill="currentColor" stroke="none" />
          <circle cx="9" cy="19" r="1" fill="currentColor" stroke="none" />
          <circle cx="15" cy="12" r="1" fill="currentColor" stroke="none" />
          <circle cx="15" cy="5" r="1" fill="currentColor" stroke="none" />
          <circle cx="15" cy="19" r="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'cursor':
      return (
        <svg {...props}>
          <path d="m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
          <path d="m13 13 6 6" />
        </svg>
      );
    case 'eraser':
      return (
        <svg {...props}>
          <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" />
          <path d="M22 21H7" />
          <path d="m5 11 9 9" />
        </svg>
      );
    case 'undo':
      return (
        <svg {...props}>
          <path d="M3 7v6h6" />
          <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
        </svg>
      );
    case 'redo':
      return (
        <svg {...props}>
          <path d="M21 7v6h-6" />
          <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
        </svg>
      );
    case 'mic':
      return (
        <svg {...props}>
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="22" />
        </svg>
      );
    case 'mic-off':
      return (
        <svg {...props}>
          <line x1="2" y1="2" x2="22" y2="22" />
          <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2" />
          <path d="M5 10v2a7 7 0 0 0 12 5" />
          <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" />
          <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
          <line x1="12" y1="19" x2="12" y2="22" />
        </svg>
      );
    case 'loader':
      return (
        <svg {...props}>
          <path d="M12 2v4" />
          <path d="M12 18v4" />
          <path d="m4.93 4.93 2.83 2.83" />
          <path d="m16.24 16.24 2.83 2.83" />
          <path d="M2 12h4" />
          <path d="M18 12h4" />
          <path d="m4.93 19.07 2.83-2.83" />
          <path d="m16.24 7.76 2.83-2.83" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
}

// ─── Clinic Mode Pills (shared across Clinic / Queue / Appointments sidebars) ───

const CLINIC_MODE_PILLS = [
  { label: 'Patients', icon: 'users', path: '/patients' },
  { label: 'Queue', icon: 'list-ordered', path: '/clinic/queue' },
  { label: 'Appointments', icon: 'calendar', path: '/appointments/providers' },
] as const;

export function ClinicModePills() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <div
      style={{
        padding: 'var(--sp-4) var(--sp-6)',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          background: 'var(--bg-surface-2)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--sp-1)',
          gap: 'var(--sp-1)',
        }}
      >
        {CLINIC_MODE_PILLS.map((item) => {
          const active =
            (item.path === '/patients' &&
              (pathname.startsWith('/patients') || pathname === '/patient/new')) ||
            (item.path === '/clinic/queue' && pathname === '/clinic/queue') ||
            (item.path.startsWith('/appointments') && pathname.startsWith('/appointments'));
          return (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--sp-1)',
                padding: 'var(--sp-2) 0',
                borderRadius: 'var(--radius-sm)',
                background: active ? 'var(--bg-surface)' : 'transparent',
                boxShadow: active ? 'var(--shadow-sm)' : 'none',
                border: 'none',
                cursor: 'pointer',
                color: active ? 'var(--text)' : 'var(--text-placeholder)',
              }}
            >
              <NavIcon name={item.icon} />
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  lineHeight: 'var(--line-height-tight)',
                  letterSpacing: '0.12px',
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Shared structural components ───

export function ContactRow({ icon, text }: { icon: string; text: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-3)',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-surface-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: 'var(--text-muted)',
        }}
      >
        <NavIcon name={icon} />
      </div>
      <span
        style={{
          fontSize: 'var(--font-size-base)',
          lineHeight: 'var(--line-height-body)',
          color: 'var(--text)',
          paddingTop: 4,
        }}
      >
        {text}
      </span>
    </div>
  );
}

// ─── Sidebar Toggle Button ───

export function SidebarToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      className="shell-toggle"
      onClick={onToggle}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      style={{
        position: 'absolute',
        top: 'calc(var(--content-inset) + 38px)',
        left: collapsed
          ? 'calc(var(--content-inset) + var(--sidebar-width-collapsed) - 18px)'
          : 'calc(var(--content-inset) + var(--sidebar-width-expanded) - 18px)',
        zIndex: 40,
        width: 36,
        height: 36,
        borderRadius: 'var(--radius-full)',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'left 200ms ease-out',
        padding: 0,
        color: 'var(--text-muted)',
      }}
    >
      <ChevronIcon direction={collapsed ? 'right' : 'left'} />
    </button>
  );
}

// ─── Sync Button ───

function SyncButton() {
  const { db } = usePowerSyncDb();
  const uploadQueue = useUploadQueueStats();
  const [syncing, setSyncing] = useState(false);

  const handleSync = useCallback(async () => {
    if (!db || syncing) return;
    if (uploadQueue.hasPending) {
      alert(
        `Cannot resync — there are ${uploadQueue.count} pending upload(s). Please wait for them to complete.`,
      );
      return;
    }
    setSyncing(true);
    try {
      await forceResync(db);
    } catch (err) {
      console.error('[SyncButton] Force resync failed:', err);
      setSyncing(false);
    }
  }, [db, syncing, uploadQueue.hasPending, uploadQueue.count]);

  return (
    <button
      onClick={handleSync}
      disabled={syncing}
      title="Force resync"
      style={{
        width: 36,
        height: 36,
        borderRadius: 'var(--radius-md)',
        background: 'transparent',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: syncing ? 'var(--color-primary)' : 'var(--text-muted)',
        cursor: syncing ? 'default' : 'pointer',
        padding: 0,
        opacity: syncing ? 0.6 : 1,
      }}
    >
      <NavIcon name="refresh-cw" />
    </button>
  );
}

// ─── User Avatar Menu ───

function UserAvatarMenu({ isActive }: { isActive?: boolean }) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Read photo_url from the local users table (reactive via PowerSync watch)
  const uid = user?.uid ?? '';
  const { data: userRows } = usePowerSyncQuery<{
    photo_url: string | null;
    display_name: string | null;
  }>({
    key: ['topbar-user-avatar', uid] as const,
    sql: `SELECT photo_url, display_name FROM users WHERE id = ? LIMIT 1`,
    params: [uid],
    watch: ['users'],
  });
  const photoUrl = userRows?.[0]?.photo_url ?? null;
  const dbDisplayName = userRows?.[0]?.display_name ?? null;

  const initials = useMemo(() => {
    const name = dbDisplayName || user?.displayName || user?.email || '';
    const parts = name.split(/[\s@]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (parts[0]?.[0] ?? '?').toUpperCase();
  }, [dbDisplayName, user?.displayName, user?.email]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-full)',
          background: photoUrl
            ? 'transparent'
            : `linear-gradient(135deg, ${primaryHex}, ${primaryHex}cc)`,
          border:
            open || isActive ? '2px solid var(--color-primary-contrast)' : '2px solid transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          cursor: 'pointer',
          padding: 0,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.02em',
          overflow: 'hidden',
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt="Avatar"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          initials
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 6,
            minWidth: 200,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 60,
            padding: 'var(--sp-2)',
          }}
        >
          {/* User info */}
          <div
            style={{
              padding: 'var(--sp-2) var(--sp-3)',
              borderBottom: '1px solid var(--border)',
              marginBottom: 'var(--sp-1)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              {dbDisplayName || user?.displayName || 'User'}
            </div>
            {user?.email && (
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                {user.email}
              </div>
            )}
          </div>

          {/* Settings */}
          <button
            onClick={() => {
              navigate('/settings/profile');
              setOpen(false);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              width: '100%',
              padding: 'var(--sp-2) var(--sp-3)',
              background: 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
          >
            <NavIcon name="gear" />
            Settings
          </button>

          {/* Sign Out */}
          <button
            onClick={() => {
              setOpen(false);
              signOut();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              width: '100%',
              padding: 'var(--sp-2) var(--sp-3)',
              background: 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
          >
            <NavIcon name="log-out" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Top App Bar (shared across all shell modes) ───

export function TopBar({ sidebarWidth }: { sidebarWidth?: number }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    tabs: patientTabs,
    activePatientId: ctxActiveId,
    closePatientTab,
    setMaxTabs,
    pendingTab,
    clearPendingTab,
    confirmPendingTab,
  } = usePatientTabs();

  const [openPatientDrawer, setOpenPatientDrawer] = useState(false);
  // Only use folder-tab style on wide screens where the tab can visually connect to content
  const [isWideScreen, setIsWideScreen] = useState(() => window.innerWidth > 1280);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1281px)');
    const handler = (e: MediaQueryListEvent) => setIsWideScreen(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const { pinnedRoutes } = usePinnedRoutes();
  const MAX_VISIBLE_PINS = 6;
  const [showPinOverflow, setShowPinOverflow] = useState(false);
  const visiblePins = pinnedRoutes.slice(0, MAX_VISIBLE_PINS);
  const overflowPins = pinnedRoutes.slice(MAX_VISIBLE_PINS);

  const isHome = pathname === '/' || pathname === '/home' || pathname === '/clinic/sync-status';
  const isPatientMode =
    pathname.startsWith('/patient/') &&
    pathname !== '/patient/new' &&
    pathname !== '/patient/register';
  const activePatientId = isPatientMode ? (ctxActiveId ?? pathname.split('/')[2]) : null;

  // ── Measure available width for patient tabs ──
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const ACTIVE_TAB_W = 150;
    const INACTIVE_TAB_W = 56;
    const TAB_GAP = 8;
    const OPEN_BTN_W = 140;
    const observer = new ResizeObserver(([entry]) => {
      const available = entry.contentRect.width - OPEN_BTN_W - TAB_GAP;
      if (available <= 0) {
        setMaxTabs(1);
        return;
      }
      // 1 active tab + N inactive tabs
      const forInactive = available - ACTIVE_TAB_W - TAB_GAP;
      const max = 1 + Math.max(0, Math.floor(forInactive / (INACTIVE_TAB_W + TAB_GAP)));
      setMaxTabs(max);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [setMaxTabs]);

  return (
    <>
      <header
        className="pc-topbar"
        style={{
          gridColumn: '1 / -1',
          height: 'var(--topbar-height)',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        }}
      >
        {/* Left section — Logo + Home (aligns with sidebar) */}
        <div
          style={{
            width: sidebarWidth != null ? sidebarWidth + 12 + 16 : undefined,
            minWidth: sidebarWidth != null ? undefined : 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding:
              sidebarWidth != null && sidebarWidth <= SIDEBAR_COLLAPSED_WIDTH
                ? '0 0 0 16px'
                : '0 0 0 var(--sp-4)',
            flexShrink: 0,
            transition: 'width 200ms ease-out',
            overflow: 'hidden',
          }}
        >
          {/* Logo — round mark when narrow, horizontal when wide */}
          <div
            style={{
              flexShrink: 0,
              width:
                sidebarWidth != null && sidebarWidth <= SIDEBAR_COLLAPSED_WIDTH
                  ? SIDEBAR_COLLAPSED_WIDTH
                  : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent:
                sidebarWidth != null && sidebarWidth <= SIDEBAR_COLLAPSED_WIDTH
                  ? 'center'
                  : undefined,
            }}
          >
            {sidebarWidth != null && sidebarWidth <= SIDEBAR_COLLAPSED_WIDTH ? (
              // Collapsed: show round logo mark
              orgLogoUrl ? (
                <img
                  src={orgLogoUrl}
                  alt={orgName}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 'var(--radius-full)',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--gradient-primary)',
                  }}
                />
              )
            ) : orgLogoHorizontalUrl ? (
              <img
                src={orgLogoHorizontalUrl}
                alt={orgName}
                style={{
                  height: 42,
                  width: 'auto',
                  objectFit: 'contain',
                  objectPosition: 'left center',
                }}
              />
            ) : orgLogoUrl ? (
              <img
                src={orgLogoUrl}
                alt={orgName}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-full)',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--gradient-primary)',
                }}
              />
            )}
          </div>

          {/* Home button — hidden when sidebar collapsed */}
          {!(sidebarWidth != null && sidebarWidth <= SIDEBAR_COLLAPSED_WIDTH) && (
            <button
              className="topbar-nav-btn"
              onClick={() => navigate('/')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                height: 36,
                padding: '0 var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                background: isHome ? 'rgba(255,255,255,0.6)' : 'transparent',
                border: 'none',
                boxShadow: isHome ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                fontSize: 'var(--font-size-base)',
                fontWeight: isHome ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
                lineHeight: 'var(--line-height-base)',
                letterSpacing: '-0.15px',
                color: isHome ? 'var(--text)' : 'var(--text-muted)',
                cursor: 'pointer',
                marginLeft: 'auto',
              }}
              title="Home"
            >
              <NavIcon name="home" />
              <span className="topbar-nav-label">Home</span>
            </button>
          )}

          {/* Pinned shortcuts — only shown when sidebar expanded */}
          {visiblePins.length > 0 &&
            !(sidebarWidth != null && sidebarWidth <= SIDEBAR_COLLAPSED_WIDTH) && (
              <>
                <span
                  style={{
                    width: 1,
                    height: 24,
                    background: 'var(--border)',
                    flexShrink: 0,
                  }}
                />
                {visiblePins.map((pin) => {
                  const isActive = pathname === pin.route || pathname.startsWith(pin.route + '/');
                  return (
                    <button
                      key={pin.key}
                      className="topbar-pin-btn"
                      onClick={() => navigate(pin.route)}
                      title={pin.label}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--sp-1)',
                        height: 32,
                        padding: '0 var(--sp-2)',
                        borderRadius: 'var(--radius-md)',
                        background: isActive ? 'var(--bg-primary-subtle)' : 'transparent',
                        border: isActive
                          ? '1px solid var(--color-primary-contrast)'
                          : '1px solid transparent',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: isActive ? 'var(--text)' : 'var(--text-muted)',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      <NavIcon name={pin.icon} />
                      <span className="topbar-pin-label">{pin.label}</span>
                    </button>
                  );
                })}
                {overflowPins.length > 0 && (
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      onClick={() => setShowPinOverflow((v) => !v)}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 'var(--radius-md)',
                        background: showPinOverflow ? 'var(--bg-hover)' : 'transparent',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-base)',
                        padding: 0,
                      }}
                    >
                      &hellip;
                    </button>
                    {showPinOverflow && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: 4,
                          minWidth: 160,
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-md)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          zIndex: 50,
                          padding: 'var(--sp-1)',
                        }}
                      >
                        {overflowPins.map((pin) => (
                          <button
                            key={pin.key}
                            onClick={() => {
                              navigate(pin.route);
                              setShowPinOverflow(false);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--sp-2)',
                              width: '100%',
                              padding: 'var(--sp-2) var(--sp-3)',
                              background: 'transparent',
                              border: 'none',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--text)',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.background = 'transparent';
                            }}
                          >
                            <NavIcon name={pin.icon} />
                            {pin.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

          {/* Divider — hidden when sidebar collapsed */}
          {!(sidebarWidth != null && sidebarWidth <= SIDEBAR_COLLAPSED_WIDTH) && (
            <span
              style={{
                width: 1,
                height: 24,
                background: 'var(--border)',
                flexShrink: 0,
                marginRight: 6,
              }}
            />
          )}
        </div>

        {/* Right section — Patient tabs + Open Patient */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
            paddingRight: 'var(--sp-2)',
          }}
        >
          {/* Home button — shown inline when sidebar collapsed */}
          {sidebarWidth != null && sidebarWidth <= SIDEBAR_COLLAPSED_WIDTH && (
            <button
              className="topbar-nav-btn"
              onClick={() => navigate('/')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                height: 36,
                padding: '0 var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                background: isHome ? 'rgba(255,255,255,0.6)' : 'transparent',
                border: 'none',
                boxShadow: isHome ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                fontSize: 'var(--font-size-base)',
                fontWeight: isHome ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
                lineHeight: 'var(--line-height-base)',
                letterSpacing: '-0.15px',
                color: isHome ? 'var(--text)' : 'var(--text-muted)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
              title="Home"
            >
              <NavIcon name="home" />
              <span className="topbar-nav-label">Home</span>
            </button>
          )}

          {/* Pinned shortcuts — shown inline when sidebar collapsed */}
          {sidebarWidth != null &&
            sidebarWidth <= SIDEBAR_COLLAPSED_WIDTH &&
            visiblePins.length > 0 && (
              <>
                <span
                  style={{
                    width: 1,
                    height: 24,
                    background: 'var(--border)',
                    flexShrink: 0,
                  }}
                />
                {visiblePins.map((pin) => {
                  const isActive = pathname === pin.route || pathname.startsWith(pin.route + '/');
                  return (
                    <button
                      key={pin.key}
                      className="topbar-pin-btn"
                      onClick={() => navigate(pin.route)}
                      title={pin.label}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--sp-1)',
                        height: 32,
                        padding: '0 var(--sp-2)',
                        borderRadius: 'var(--radius-md)',
                        background: isActive ? 'var(--bg-primary-subtle)' : 'transparent',
                        border: isActive
                          ? '1px solid var(--color-primary-contrast)'
                          : '1px solid transparent',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: isActive ? 'var(--text)' : 'var(--text-muted)',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      <NavIcon name={pin.icon} />
                      <span className="topbar-pin-label">{pin.label}</span>
                    </button>
                  );
                })}
                {overflowPins.length > 0 && (
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      onClick={() => setShowPinOverflow((v) => !v)}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 'var(--radius-md)',
                        background: showPinOverflow ? 'var(--bg-hover)' : 'transparent',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-base)',
                        padding: 0,
                      }}
                    >
                      &hellip;
                    </button>
                    {showPinOverflow && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: 4,
                          minWidth: 160,
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-md)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          zIndex: 50,
                          padding: 'var(--sp-1)',
                        }}
                      >
                        {overflowPins.map((pin) => (
                          <button
                            key={pin.key}
                            onClick={() => {
                              navigate(pin.route);
                              setShowPinOverflow(false);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--sp-2)',
                              width: '100%',
                              padding: 'var(--sp-2) var(--sp-3)',
                              background: 'transparent',
                              border: 'none',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--text)',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.background = 'transparent';
                            }}
                          >
                            <NavIcon name={pin.icon} />
                            {pin.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <span
                  style={{
                    width: 1,
                    height: 24,
                    background: 'var(--border)',
                    flexShrink: 0,
                  }}
                />
              </>
            )}

          {/* Patient tabs container — measured for dynamic max tabs */}
          <div
            ref={tabsContainerRef}
            style={{
              flex: 1,
              minWidth: 0,
              height: '100%',
              display: 'flex',
              alignItems: 'flex-end',
              gap: 'var(--sp-1)',
            }}
          >
            {patientTabs.map((tab) => {
              const isActive = tab.id === activePatientId;
              if (isActive) {
                // In patient mode: folder tab connects to paper
                // In other modes: just a highlighted flat tab
                const isFolderTab = isPatientMode && isWideScreen;
                return (
                  <button
                    key={tab.id}
                    onClick={() => navigate(`/patient/${tab.id}`)}
                    style={
                      isFolderTab
                        ? {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 'var(--sp-2)',
                            alignSelf: 'flex-end',
                            height: 'calc(var(--topbar-height) - 8px + var(--radius-lg))',
                            marginBottom: 'calc(-1 * var(--radius-lg))',
                            padding: '0 var(--sp-4) var(--radius-lg)',
                            borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
                            background: '#ffffff',
                            border: 'none',
                            boxShadow: '0 -1px 4px rgba(0,0,0,0.06)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-semi)',
                            lineHeight: 'var(--line-height-base)',
                            color: 'var(--text)',
                            cursor: 'pointer',
                            flexShrink: 0,
                            position: 'relative',
                            zIndex: 51,
                          }
                        : {
                            display: 'inline-flex',
                            alignItems: 'center',
                            alignSelf: 'center',
                            gap: 'var(--sp-2)',
                            height: 36,
                            padding: '0 var(--sp-4)',
                            borderRadius: 'var(--radius-full)',
                            background: '#ffffff',
                            border: 'none',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 'var(--font-weight-semi)',
                            lineHeight: 'var(--line-height-base)',
                            color: 'var(--text)',
                            cursor: 'pointer',
                            flexShrink: 0,
                          }
                    }
                  >
                    {tab.photoUrl ? (
                      <img
                        src={tab.photoUrl}
                        alt={tab.label}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 'var(--radius-full)',
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 'var(--radius-full)',
                          background: avatarGradient(tab.color),
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {tab.fullName || tab.label}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        closePatientTab(tab.id);
                        if (isActive) navigate('/');
                      }}
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 'var(--radius-xs)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--text-muted)',
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </div>
                  </button>
                );
              }
              return (
                <button
                  key={tab.id}
                  onClick={() => navigate(`/patient/${tab.id}`)}
                  style={
                    isWideScreen
                      ? {
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--sp-1)',
                          alignSelf: 'flex-end',
                          height: 'calc(var(--topbar-height) - 12px + var(--radius-lg))',
                          marginBottom: 'calc(-1 * var(--radius-lg))',
                          padding: '0 var(--sp-3) var(--radius-lg)',
                          borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
                          background: 'rgba(255,255,255,0.4)',
                          border: 'none',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          lineHeight: 'var(--line-height-base)',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          flexShrink: 0,
                          position: 'relative',
                          zIndex: 49,
                        }
                      : {
                          display: 'inline-flex',
                          alignItems: 'center',
                          alignSelf: 'center',
                          justifyContent: 'center',
                          height: 30,
                          padding: '0 var(--sp-3)',
                          borderRadius: 'var(--radius-full)',
                          background: 'rgba(255,255,255,0.45)',
                          border: 'none',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          lineHeight: 'var(--line-height-base)',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          flexShrink: 0,
                        }
                  }
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 'var(--radius-full)',
                      background: avatarGradient(tab.color),
                      flexShrink: 0,
                    }}
                  />
                  {tab.label}
                </button>
              );
            })}

            {/* + Open Patient button */}
            <button
              className="topbar-nav-btn"
              onClick={() => setOpenPatientDrawer(true)}
              title="Open Patient"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'center',
                gap: 'var(--sp-1)',
                height: 32,
                padding: '0 var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                background: 'transparent',
                border: 'none',
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-medium)',
                lineHeight: 'var(--line-height-base)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <NavIcon name="folder-open" />
              <span className="topbar-nav-label">Open Patient</span>
            </button>
          </div>

          {/* Right section: Clinic dropdown, AI, User Avatar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
            }}
          >
            <SyncButton />
            <UserAvatarMenu isActive={isHome} />
          </div>
        </div>
      </header>

      <OpenPatientDrawer open={openPatientDrawer} onOpenChange={setOpenPatientDrawer} />

      {pendingTab && (
        <ManagePatientTabsDrawer
          open={!!pendingTab}
          onOpenChange={(open) => {
            if (!open) clearPendingTab();
          }}
          tabs={patientTabs}
          activePatientId={activePatientId}
          pendingTab={pendingTab}
          onConfirm={confirmPendingTab}
          onCancel={clearPendingTab}
        />
      )}
    </>
  );
}

// ─── Icon Rail (shared across all shell modes) ───

const RAIL_ICONS = [
  // { name: 'layout-grid', label: 'Dashboard', route: '/dashboard', href: '/dashboard' },
  { name: 'users', label: 'Patients', route: '/patient', href: '/patient/new' },
  {
    name: 'calendar',
    label: 'Appointments',
    route: '/appointments',
    href: '/appointments/providers',
  },
  { name: 'list-ordered', label: 'Queue', route: '/clinic/queue', href: '/clinic/queue' },
  // { name: 'file-text', label: 'Records', route: '/records', href: '/records' },
  // { name: 'bar-chart', label: 'Reports', route: '/reports', href: '/reports' },
  // { name: 'credit-card', label: 'Billing', route: '/billing', href: '/billing' },
  // { name: 'message-square', label: 'Messages', route: '/messages', href: '/messages' },
] as const;

// Patient mode: no rail icons (hidden), only bottom Help/Settings remain
// const PATIENT_RAIL_ICONS = [
//   { name: 'stethoscope', label: 'Consultation' },
//   { name: 'calendar', label: 'Calendar' },
//   { name: 'users', label: 'Patients' },
// ] as const;

export function IconRail() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Detect patient mode: /patient/:id but NOT /patient/new
  const isPatientMode =
    pathname.startsWith('/patient/') &&
    pathname !== '/patient/new' &&
    pathname !== '/patient/register';
  // Settings gear is active when on root (Common/Settings mode)
  const isHomeActive = pathname === '/' || pathname === '/clinic/sync-status';

  const railButtonStyle = (isActive: boolean) => ({
    width: 40,
    height: 40,
    borderRadius: 'var(--radius-lg)',
    background: isActive ? 'var(--bg-primary-subtle)' : 'transparent',
    border: isActive ? '2px solid var(--border-strong)' : 'none',
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    color: isActive ? 'var(--color-primary)' : 'var(--text-muted)',
    cursor: 'pointer',
    padding: 0,
  });

  const RailButton = ({
    label,
    isActive,
    onClick,
    iconName,
  }: {
    label: string;
    isActive: boolean;
    onClick: () => void;
    iconName: string;
  }) => (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
      className="rail-btn-wrap"
    >
      <button
        aria-label={label}
        onClick={onClick}
        style={railButtonStyle(isActive)}
        className="rail-btn"
        data-active={isActive || undefined}
      >
        <NavIcon name={iconName} />
      </button>
      <span
        className="rail-label"
        data-active={isActive || undefined}
        style={{
          position: 'absolute',
          top: '100%',
          fontSize: 9,
          fontWeight: 600,
          color: isActive ? 'var(--color-primary)' : 'var(--text-muted)',
          marginTop: 1,
          whiteSpace: 'nowrap',
          textAlign: 'center',
          letterSpacing: '0.02em',
        }}
      >
        {label}
      </span>
    </div>
  );

  return (
    <nav
      className="icon-rail pc-sidebar"
      style={{
        width: 'var(--rail-width)',
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 'var(--sp-6)',
        gap: 'var(--sp-2)',
      }}
    >
      <style>{`
        .icon-rail .rail-label { opacity: 0; transition: opacity 0.15s; pointer-events: none; }
        .icon-rail:hover .rail-label { opacity: 1; }
        .icon-rail .rail-btn-wrap .rail-btn { transition: color 0.15s; }
        .icon-rail:hover .rail-btn-wrap .rail-btn:not([data-active="true"]) { color: var(--text) !important; }
        .icon-rail:hover .rail-btn-wrap .rail-label:not([data-active="true"]) { color: var(--text) !important; }
      `}</style>
      {isPatientMode
        ? null
        : RAIL_ICONS.map((icon) => {
            const isActive = pathname.startsWith(icon.route);
            return (
              <RailButton
                key={icon.name}
                label={icon.label}
                isActive={isActive}
                onClick={() => navigate(icon.href)}
                iconName={icon.name}
              />
            );
          })}

      <div style={{ flex: 1 }} />

      {/* Bottom utility buttons */}
      <RailButton label="Help" isActive={false} onClick={() => {}} iconName="info" />
      <div style={{ marginBottom: 'var(--sp-6)' }}>
        <RailButton
          label="Settings"
          isActive={isHomeActive}
          onClick={() => navigate('/')}
          iconName="settings"
        />
      </div>
    </nav>
  );
}

// ─── Edge Glow (collapsed sidebar indicator) ───

export function EdgeGlow({ topOffset }: { topOffset?: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: topOffset ?? 'var(--content-title-height)',
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 5,
      }}
    >
      {/* Gradient layer — 12px wide fade */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 12,
          background: 'linear-gradient(to right, var(--sidebar-edge-gradient-from), transparent)',
        }}
      />
      {/* Line layer — 2px solid with glow */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 2,
          background: 'var(--sidebar-edge-color)',
          boxShadow: 'var(--sidebar-edge-shadow)',
        }}
      />
    </div>
  );
}
